import {
  type AdminAuthSession,
  type AuthCredentials,
  type RegistrationResult,
  getAuthSessionKey
} from "./access";

interface ApiErrorPayload {
  error?: string;
  message?: string;
}

export async function registerAdmin(credentials: AuthCredentials): Promise<RegistrationResult> {
  return authRequest<RegistrationResult>("/api/auth/register", {
    method: "POST",
    body: credentials
  });
}

export async function loginAdmin(credentials: Pick<AuthCredentials, "email" | "password">): Promise<AdminAuthSession> {
  return authRequest<AdminAuthSession>("/api/auth/login", {
    method: "POST",
    body: credentials
  });
}

export async function confirmAdminEmail(token: string): Promise<{ success: boolean; email: string; emailConfirmed: boolean }> {
  return authRequest("/api/auth/confirm-email", {
    method: "POST",
    body: { token }
  });
}

export async function resendAdminConfirmation(email: string): Promise<RegistrationResult> {
  return authRequest<RegistrationResult>("/api/auth/resend-confirmation", {
    method: "POST",
    body: { email }
  });
}

export async function loadAdminSession(sessionKey = getAuthSessionKey()): Promise<AdminAuthSession> {
  return authRequest<AdminAuthSession>("/api/auth/session", {
    sessionKey
  });
}

export async function switchAdminOrg(orgId: string, sessionKey = getAuthSessionKey()): Promise<AdminAuthSession> {
  return authRequest<AdminAuthSession>("/api/auth/switch-org", {
    method: "POST",
    sessionKey,
    body: { orgId }
  });
}

export async function logoutAdmin(sessionKey = getAuthSessionKey()): Promise<void> {
  await authRequest("/api/auth/logout", {
    method: "POST",
    sessionKey
  });
}

async function authRequest<T>(
  path: string,
  options: { method?: "GET" | "POST"; sessionKey?: string | null; body?: unknown } = {}
): Promise<T> {
  const headers = new Headers({
    accept: "application/json"
  });

  if (options.sessionKey) {
    headers.set("x-user-key", options.sessionKey);
  }

  if (options.body !== undefined) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(path, {
    method: options.method || "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });

  const payload = await readPayload(response);
  if (!response.ok) {
    const message = extractErrorMessage(payload) || `Request failed with ${response.status}`;
    throw Object.assign(new Error(message), { status: response.status });
  }

  return payload as T;
}

async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { text };
  }
}

function extractErrorMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as ApiErrorPayload;
  return typeof record.error === "string"
    ? record.error
    : typeof record.message === "string"
      ? record.message
      : null;
}
